/**
 * Session management via React Query.
 *
 * Replaces the old useSessions hook (useState + manual fetch) with:
 * - useQuery for session list (auto-refetch, caching)
 * - useMutation for create/delete
 * - queryClient.invalidateQueries for cache invalidation
 */

import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { apiFetch } from "@/shared/api";
import { sessionsQueryOptions, sessionMetaQueryOptions } from "../queries/session-queries";
import type { SessionMeta } from "../types";
import { useChatStore, useChatStoreApi } from "../stores";
import { useFolderSyncStore } from "../stores/folder-sync-store";
import { currentUserId, removeHandle } from "../lib/handleStore";

export function useSessions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const storeApi = useChatStoreApi();
  // Shared across routes — local useState would desync ChatPage and /agents.
  const activeId = useChatStore((s) => s.activeSessionId);
  // Navigates to `/?sid=…` for a session, or to `/new` (hero) for null.
  // Hero gets its own route because `/` is the smart landing — admins are
  // bounced from `/` to `/company`, so it can't host the new-task state.
  // IndexRoute mirrors a non-null URL.sid back into the store but does NOT
  // sync the empty-URL direction (race with sendMessage's pushed messages
  // — see _app.index.tsx). So explicit "drop the active session" callers
  // (startNew → hero, deleted-session bounce) clear the store themselves
  // before navigating.
  const setActiveId = useCallback((id: string | null) => {
    if (id === null) {
      storeApi.getState().setActiveSession(null);
      navigate({ to: "/new" });
      return;
    }
    navigate({ to: "/", search: { sid: id } });
  }, [navigate, storeApi]);

  const { data: sessions = [] } = useQuery(sessionsQueryOptions());

  const createSession = useCallback(async (
    agentRef?: string,
    connectorIds?: string[] | null,
  ): Promise<string> => {
    const body: Record<string, unknown> = {};
    if (agentRef) body.agent_ref = agentRef;
    if (connectorIds) body.connector_ids = connectorIds;
    const resp = await apiFetch("/api/sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const session = await resp.json();
    // Seed the cache with the full session so components depending on the
    // agent snapshot (e.g. SkillsPanel filtering by agent.skill_ids) render
    // correctly from the first paint — otherwise there's a flash where the
    // panel shows the full catalogue until GET /api/sessions refetches.
    queryClient.setQueryData(sessionsQueryOptions().queryKey, (old) =>
      old ? [session, ...old] : [session],
    );
    // Also seed /sessions/{id}/meta — the chat sidebar reads `agent` from
    // there to dodge the list endpoint's status='active' filter (drafts
    // are invisible to /sessions until the first turn promotes them).
    // Empty for everything except `agent` and `is_owner`; the real /meta
    // refetch fills the rest before any of those fields render.
    queryClient.setQueryData<SessionMeta>(
      sessionMetaQueryOptions(session.id).queryKey,
      {
        session_id: session.id,
        title: session.title,
        owner: null,
        is_owner: true,
        claimable: false,
        claim_blocked_reason: null,
        visibility: "private",
        space_role: "editor",
        agent: session.agent,
      },
    );
    return session.id;
  }, [queryClient]);

  const startNew = useCallback(() => {
    setActiveId(null);
  }, [setActiveId]);

  const deleteSessionMutation = useMutation({
    mutationFn: async (id: string) => {
      // Stop folder sync before the DELETE wipes the workspace, else the
      // server→local reconciliation deletes the user's local originals.
      // Single chokepoint — covers every delete path (header, list, history).
      const fs = useFolderSyncStore.getState();
      if (fs.connectedSessionId === id) fs.disconnect();
      await apiFetch(`/api/sessions/${id}`, { method: "DELETE" });
      return id;
    },
    // Clear the active id in the store but DO NOT touch the URL — callers
    // are responsible for navigating (chat header walks history back; list
    // pages stay where they are). Forcing navigate({to:"/"}) here would
    // yank the user out of /history or /spaces/X, and would also override
    // a deliberate history.back() the caller did just before mutate().
    onSuccess: (deletedId) => {
      if (activeId === deletedId) storeApi.getState().setActiveSession(null);
      const uid = currentUserId();
      if (uid) removeHandle(uid, deletedId).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const deleteSession = useCallback(
    (id: string) => deleteSessionMutation.mutate(id),
    [deleteSessionMutation],
  );

  const selectSession = useCallback((id: string) => {
    setActiveId(id);
  }, [setActiveId]);

  /** Update a session title in the cache (optimistic, from CUSTOM event). */
  const updateSessionTitle = useCallback(
    (id: string, title: string) => {
      queryClient.setQueryData(sessionsQueryOptions().queryKey, (old) =>
        old?.map((s) => (s.id === id ? { ...s, title } : s)),
      );
    },
    [queryClient],
  );

  /** Invalidate session list — call after streaming ends, session create, etc. */
  const invalidateSessions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["sessions"] });
  }, [queryClient]);

  return {
    sessions,
    activeId,
    setActiveId,
    createSession,
    startNew,
    deleteSession,
    selectSession,
    updateSessionTitle,
    invalidateSessions,
  };
}
